/**
 * Contact-request domain — React Query hooks.
 *
 * Read hooks are re-exported from the generated client. The hand-written
 * wrappers (`useGetContactRequestsInfinite`, `useHasPendingContactRequest`)
 * express things Kubb can't: an infinite query and a polling list probe.
 *
 * Write hooks go through `useInvalidatingMutation` so cache invalidation is
 * declarative and compile-time-enforced via the `ContactRequestMutationId`
 * registry in `./invalidation`.
 */

import { useCallback } from 'react'
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { notifySuccess } from '../toast'
import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'
import {
  fetchContactRequestJobs,
  isPendingContactRequestStatus,
  type ContactRequestJobPaginatedResponse,
} from './utils'

import { createContactRequestJobFromCampaign } from '../generated/clients/createContactRequestJobFromCampaign'
import { createCreditCheckoutSession } from '../generated/clients/createCreditCheckoutSession'

import type { CreateContactRequestJobFromCampaignMutationRequest } from '../generated/types/CreateContactRequestJobFromCampaign'
import type { CreateCreditCheckoutSessionMutationRequest } from '../generated/types/CreateCreditCheckoutSession'

// ── Generated query-hook re-exports (READ) ─────────────────────────

export {
  useCheckContactRequestStatus,
  checkContactRequestStatusQueryKey,
  useGetContactRequestJobDetail,
  getContactRequestJobDetailQueryKey,
  useGetCreditBalance,
  getCreditBalanceQueryKey,
  useListContactRequestJobs,
  listContactRequestJobsQueryKey,
  useListRequestedContacts,
  listRequestedContactsQueryKey,
} from '../generated/hooks/contactRequestController'

// ── Generated query/mutation type re-exports ───────────────────────

export type {
  CheckContactRequestStatusQueryParams,
  CheckContactRequestStatusQueryResponse,
} from '../generated/types/CheckContactRequestStatus'
export type {
  GetContactRequestJobDetailQueryResponse,
  GetContactRequestJobDetailPathParams,
} from '../generated/types/GetContactRequestJobDetail'
export type { GetCreditBalanceQueryResponse } from '../generated/types/GetCreditBalance'
export type {
  ListContactRequestJobsQueryParams,
  ListContactRequestJobsQueryResponse,
} from '../generated/types/ListContactRequestJobs'
export type {
  ListRequestedContactsQueryParams,
  ListRequestedContactsQueryResponse,
  ListRequestedContactsPathParams,
} from '../generated/types/ListRequestedContacts'
export type {
  CreateContactRequestJobFromCampaignMutationRequest,
  CreateContactRequestJobFromCampaignMutationResponse,
} from '../generated/types/CreateContactRequestJobFromCampaign'
export type {
  CreateCreditCheckoutSessionMutationRequest,
  CreateCreditCheckoutSessionMutationResponse,
} from '../generated/types/CreateCreditCheckoutSession'

// ── Hand-written read hooks ────────────────────────────────────────

/**
 * Contact request jobs with infinite-scroll pagination (mobile list).
 *
 * Wraps the generated `listContactRequestJobs` client fn (via the
 * `fetchContactRequestJobs` helper) in `useInfiniteQuery` — Kubb doesn't emit
 * infinite-query variants. Stops paging once a page returns fewer than
 * `limit` rows.
 */
export function useGetContactRequestsInfinite(params: {
  limit: number
  page: number
}) {
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['contact_requests', params] as const,
    queryFn: ({ pageParam = 1 }): Promise<ContactRequestJobPaginatedResponse> =>
      fetchContactRequestJobs({ ...params, page: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.results.length < params.limit) return undefined
      return allPages.length + 1
    },
  })
}

/**
 * Detects whether a campaign has an in-progress contact request, i.e. one that
 * has been submitted but not yet completed, rejected, or cancelled.
 *
 * Fetches the single newest job (`ordering: '-created'`, `limit: 1`) and reads
 * its collapsed status. While a request is pending, polls every 30s so the
 * zero state self-corrects when the request resolves, then stops.
 */
export function useHasPendingContactRequest(
  campaignId: number,
  enabled = true
) {
  return useQuery({
    queryKey: ['campaign', campaignId, 'has-pending-contact-request'],
    queryFn: async () => {
      const { results } = await fetchContactRequestJobs({
        campaign: campaignId,
        ordering: '-created',
        limit: 1,
        page: 1,
      })
      const latestJob = results[0]
      return latestJob
        ? isPendingContactRequestStatus(latestJob.status.id)
        : false
    },
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchInterval: (query) => (query.state.data === true ? 30_000 : false),
  })
}

/**
 * Returns a callback that busts the cached contact-request state for a
 * campaign: the Contact Requests badge count and the pending-request flag.
 * Call it after a request is created so the Contacts tab flips to "in
 * progress" and the badge updates immediately instead of waiting out
 * `staleTime`.
 */
export function useInvalidateCampaignContactRequests(campaignId: number) {
  const client = useQueryClient()
  return useCallback(() => {
    void client.invalidateQueries({
      queryKey: ['campaign', campaignId, 'contact-requests-count'],
    })
    void client.invalidateQueries({
      queryKey: ['campaign', campaignId, 'has-pending-contact-request'],
    })
  }, [client, campaignId])
}

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

/**
 * Request contacts from a campaign's targets. Invalidates the job list, credit
 * balance, and the campaign's badge/pending caches on success (see registry).
 */
export function useRequestContactsFromCampaign() {
  return useInvalidatingMutation(
    'createContactRequestJobFromCampaign',
    invalidationRegistry,
    {
      mutationFn: (
        variables: CreateContactRequestJobFromCampaignMutationRequest
      ) => createContactRequestJobFromCampaign(variables),
      onSuccess: () =>
        notifySuccess('Contacts requested from campaign successfully'),
    }
  )
}

/**
 * Create a Stripe checkout session to purchase contact credits. Returns the
 * session `{ session_id, url }`; the caller redirects to `url`.
 */
export function useCreateCheckoutSession() {
  return useInvalidatingMutation(
    'createCreditCheckoutSession',
    invalidationRegistry,
    {
      mutationFn: (variables: CreateCreditCheckoutSessionMutationRequest) =>
        createCreditCheckoutSession(variables),
    }
  )
}
